import { dia, shapes } from '@joint/core'
import { minAreaRect } from './minAreaRect'

export type Vertex = [number, number]
export type ElementSnapshot = ReturnType<dia.Element['toJSON']>

/** Geometry in the current map coordinates, including moves since import. */
export function snapshotVertices(snapshot: ElementSnapshot): Vertex[] {
  const pos = snapshot.position ?? { x: 0, y: 0 }
  const data = snapshot.data ?? {}
  const origin = data.originalPosition ?? pos
  const vertices: Vertex[] = data.vertices ?? []
  return vertices.length ? vertices.map(([x, y]) => [x + (pos.x - origin.x), y + (pos.y - origin.y)]) : [[pos.x, pos.y]]
}

export function isLightGeometry(element: dia.Element): boolean {
  const data = element.get('data') ?? {}
  return ['l-light', 'l-lightingline', 'c-light'].includes(data.layer ?? data.groupName)
}

/** Angle of the long side in screen coordinates (clockwise, modulo 180°). */
export function frameAngle(vertices: Vertex[]): number {
  let angle = 0
  let longest = 0
  for (let i = 0; i < vertices.length; i++) {
    const a = vertices[i]
    const b = vertices[(i + 1) % vertices.length]
    const length = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (length > longest + 1e-8) {
      longest = length
      angle = ((Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI + 180) % 180
    }
  }
  return angle
}

export function frameVertices(points: Vertex[], oriented = true): Vertex[] {
  // A single segment (or several collinear segments) needs a thin rotated
  // frame; minAreaRect's generic degenerate fallback is axis-aligned.
  const first = points[0]
  const second = points.find((point) => Math.hypot(point[0] - first[0], point[1] - first[1]) > 1e-8)
  if (oriented && second) {
    const length = Math.hypot(second[0] - first[0], second[1] - first[1])
    const ux = (second[0] - first[0]) / length
    const uy = (second[1] - first[1]) / length
    if (points.every(([x, y]) => Math.abs(-(x - first[0]) * uy + (y - first[1]) * ux) < 1e-8)) {
      const projections = points.map(([x, y]) => (x - first[0]) * ux + (y - first[1]) * uy)
      const lo = Math.min(...projections)
      const hi = Math.max(...projections)
      return [
        [lo, -0.0005],
        [hi, -0.0005],
        [hi, 0.0005],
        [lo, 0.0005],
      ].map(([u, v]) => [first[0] + u * ux - v * uy, first[1] + u * uy + v * ux])
    }
  }
  const rectangle = minAreaRect(points.map(([x, y]) => ({ x, y })))
  if (oriented && rectangle.length === 4) {
    const area =
      Math.abs(
        rectangle.reduce((sum, p, i) => {
          const q = rectangle[(i + 1) % rectangle.length]
          return sum + p.x * q.y - q.x * p.y
        }, 0)
      ) / 2
    if (area > 1e-8) return rectangle.map((p) => [p.x, p.y])
  }
  const minX = Math.min(...points.map((p) => p[0]))
  const minY = Math.min(...points.map((p) => p[1]))
  const maxX = Math.max(minX + 0.001, ...points.map((p) => p[0]))
  const maxY = Math.max(minY + 0.001, ...points.map((p) => p[1]))
  return [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ]
}

/** Store the same polygon for canvas, SVG, hit testing, persistence and export. */
export function writePolygon(element: dia.Element, vertices: Vertex[], closed = true): void {
  const x = Math.min(...vertices.map((p) => p[0]))
  const y = Math.min(...vertices.map((p) => p[1]))
  const width = Math.max(0.001, Math.max(...vertices.map((p) => p[0])) - x)
  const height = Math.max(0.001, Math.max(...vertices.map((p) => p[1])) - y)
  const d = vertices.map(([px, py], i) => `${i ? 'L' : 'M'} ${px - x} ${py - y}`).join(' ') + (closed ? ' Z' : '')
  // Older saved light groups are Rectangle cells. Override markup as well so
  // their SVG view and a subsequent reload use the new path too.
  element.set('type', 'standard.Path')
  element.set('markup', [
    { tagName: 'path', selector: 'body' },
    { tagName: 'text', selector: 'label' },
  ])
  element.position(x, y)
  element.resize(width, height)
  element.attr('body/d', d)
  element.set('data', { ...element.get('data'), vertices, originalPosition: { x, y } })
}

/** Translate snapshots into the group's current coordinate system before editing. */
export function currentOriginals(element: dia.Element): ElementSnapshot[] {
  const data = element.get('data') ?? {}
  const originals: ElementSnapshot[] = data.originalElements ?? []
  const origin = data.originalPosition ?? element.position()
  const pos = element.position()
  return originals.map((original) =>
    transformSnapshot(original, ([x, y]) => [x + pos.x - origin.x, y + pos.y - origin.y])
  )
}

function transformSnapshot(snapshot: ElementSnapshot, transform: (p: Vertex) => Vertex): ElementSnapshot {
  const result = JSON.parse(JSON.stringify(snapshot)) as ElementSnapshot
  delete result.id
  const data = result.data ?? {}
  if (data.vertices?.length > 1 && data.type !== 'point') {
    const vertices = snapshotVertices(snapshot).map(transform)
    const temp = new shapes.standard.Path(result)
    writePolygon(temp, vertices, data.type !== 'line')
    Object.assign(result, temp.toJSON())
    delete result.id
  } else {
    const [x, y] = transform([result.position.x, result.position.y])
    result.position = { x, y }
    if (data.vertices?.length) {
      result.data.vertices = snapshotVertices(snapshot).map(transform)
      result.data.originalPosition = { x, y }
    }
  }
  if (data.originalElements) {
    const origin = data.originalPosition ?? snapshot.position
    const dx = snapshot.position.x - origin.x
    const dy = snapshot.position.y - origin.y
    result.data.originalElements = data.originalElements.map((child: ElementSnapshot) =>
      transformSnapshot(child, ([x, y]) => transform([x + dx, y + dy]))
    )
  }
  return result
}

/** Keep edited light paths and exported/restorable vertices in sync on resize. */
export function resizeLightGeometry(element: dia.Element, width: number, height: number): void {
  const size = element.size()
  if (width <= 0 || height <= 0 || !Number.isFinite(width + height) || size.width <= 0 || size.height <= 0) return
  const pos = element.position()
  const transform = ([x, y]: Vertex): Vertex => [
    pos.x + ((x - pos.x) * width) / size.width,
    pos.y + ((y - pos.y) * height) / size.height,
  ]
  const originals = currentOriginals(element).map((original) => transformSnapshot(original, transform))
  const vertices = snapshotVertices(element.toJSON()).map(transform)
  writePolygon(element, vertices, element.get('data')?.type !== 'line')
  element.set('data', {
    ...element.get('data'),
    geometryAngle: frameAngle(frameVertices(vertices)),
    ...(originals.length ? { originalElements: originals } : {}),
  })
}

/** Recover the original CAD orientation even for a previously saved AABB group. */
export function fitLightFrame(element: dia.Element): void {
  const originals = currentOriginals(element)
  const points = originals.length ? originals.flatMap(snapshotVertices) : snapshotVertices(element.toJSON())
  const vertices = frameVertices(points)
  writePolygon(element, vertices)
  element.set('data', { ...element.get('data'), geometryAngle: frameAngle(vertices), originalElements: originals })
}

/** Rotate actual vertices, including restorable group members, about the center. */
export function rotateLightGeometry(element: dia.Element, angle: number): void {
  if (!Number.isFinite(angle)) return
  const vertices = snapshotVertices(element.toJSON())
  if (vertices.length < 2) return
  const current = element.get('data')?.geometryAngle ?? frameAngle(frameVertices(vertices))
  const radians = ((angle - current) * Math.PI) / 180
  const pos = element.position()
  const size = element.size()
  const center: Vertex = [pos.x + size.width / 2, pos.y + size.height / 2]
  const transform = ([x, y]: Vertex): Vertex => [
    center[0] + (x - center[0]) * Math.cos(radians) - (y - center[1]) * Math.sin(radians),
    center[1] + (x - center[0]) * Math.sin(radians) + (y - center[1]) * Math.cos(radians),
  ]
  const originals = currentOriginals(element).map((original) => transformSnapshot(original, transform))
  writePolygon(element, vertices.map(transform), element.get('data')?.type !== 'line')
  element.set('data', {
    ...element.get('data'),
    geometryAngle: angle,
    ...(originals.length ? { originalElements: originals } : {}),
  })
}
